/**
 * Paywall store — opened by the axios interceptor on
 * 429 + body.error === 'Feature_limit_reached' (CheckPlanFeature middleware).
 *
 * Modal UI consumer lands in SP-038 (<UsageLimitModal />). Until then, the
 * store is a no-op state holder; the interceptor wiring still flips `open`
 * so any consumer mounted later picks up the latest payload.
 */

import { create } from "zustand";

import type { Metric } from "@/services/billing";

export interface PaywallState {
  open: boolean;
  metric: Metric | null;
  planLimit: number;
  planUsed: number;
  creditsAvailable: number;
  resetAt: string | null;
}

const initialState: PaywallState = {
  open: false,
  metric: null,
  planLimit: 0,
  planUsed: 0,
  creditsAvailable: 0,
  resetAt: null,
};

export interface FeatureLimitPayload {
  metric: Metric;
  plan_limit: number;
  plan_used: number;
  credits_purchased?: number;
  credits_used?: number;
  reset_at: string | null;
}

interface PaywallStore {
  paywall: PaywallState;
  openFromApi429: (data: FeatureLimitPayload) => void;
  close: () => void;
}

export const usePaywallStore = create<PaywallStore>((set) => ({
  paywall: { ...initialState },
  openFromApi429: (data) =>
    set({
      paywall: {
        open: true,
        metric: data.metric,
        planLimit: data.plan_limit,
        planUsed: data.plan_used,
        creditsAvailable:
          (data.credits_purchased ?? 0) - (data.credits_used ?? 0),
        resetAt: data.reset_at,
      },
    }),
  close: () => set((s) => ({ paywall: { ...s.paywall, open: false } })),
}));
